import api from './api';

export interface RegraPLR {
  id?: string;
  nome: string;
  periodo: string;
  status: 'ABERTA' | 'FECHADA' | 'PROCESSADA';
  okrsPesos?: OKRPeso[];
  colaboradores?: string[];
  valorTotalDisponivel?: number;
  criteriosAdicionais?: string;
  empresaId?: string;
  dataInicio?: string;
  dataFim?: string;
  dataCriacao?: string;
  dataProcessamento?: string;
}

export interface OKRPeso {
  okrId: string;
  okrNome: string;
  peso: number;
  progressoAtual?: number;
}

export interface ResultadoPLR {
  id?: string;
  regraId: string;
  regraNome?: string;
  colaboradorId: string;
  colaboradorNome?: string;
  progressoGeral: number;
  valorCalculado: number;
  valorFinal?: number;
  observacoes?: string;
  status: 'CALCULADO' | 'APROVADO' | 'PAGO';
}

export interface PLRGeracaoDto {
  okrIds: string[];
  colaboradoresIds?: string[];
  periodo: string;
  valorTotalDisponivel?: number;
  contextoAdicional?: string;
}

export interface ResultadoConsulta<T> {
  resultados: T[];
  total: number;
  pagina: number;
  tamanho: number;
  totalPaginas: number;
}

class PLRService {
  
  /**
   * Lista todas as regras de PLR
   */
  async listar(): Promise<RegraPLR[]> {
    try {
      const response = await api.get('/api/plr');
      return response.data;
    } catch (error) {
      console.error('Erro ao listar regras de PLR:', error);
      throw error;
    }
  }

  /**
   * Busca regras de PLR com filtros
   */
  async buscar(filtros: {
    status?: string;
    periodoInicio?: string;
    periodoFim?: string;
  }): Promise<ResultadoConsulta<RegraPLR>> {
    try {
      const params = new URLSearchParams();
      Object.entries(filtros).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await api.get(`/api/plr/buscar?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar regras de PLR:', error);
      throw error;
    }
  }

  /**
   * Obtém uma regra de PLR por ID
   */
  async obterPorId(id: string): Promise<RegraPLR> {
    try {
      const response = await api.get(`/api/plr/${id}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao obter regra de PLR:', error);
      throw error;
    }
  }

  /**
   * Cria uma nova regra de PLR
   */
  async criar(regra: Omit<RegraPLR, 'id'>): Promise<RegraPLR> {
    try {
      const response = await api.post('/api/plr', regra);
      return response.data;
    } catch (error) {
      console.error('Erro ao criar regra de PLR:', error);
      throw error;
    }
  }

  /**
   * Atualiza uma regra de PLR existente
   */
  async atualizar(id: string, regra: Partial<RegraPLR>): Promise<RegraPLR> {
    try {
      const response = await api.put(`/api/plr/${id}`, regra);
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar regra de PLR:', error);
      throw error;
    }
  }

  /**
   * Exclui uma regra de PLR
   */
  async excluir(id: string): Promise<void> {
    try {
      await api.delete(`/api/plr/${id}`);
    } catch (error) {
      console.error('Erro ao excluir regra de PLR:', error);
      throw error;
    }
  }

  /**
   * Fecha o período de uma regra de PLR
   */
  async fecharPeriodo(id: string): Promise<RegraPLR> {
    try {
      const response = await api.put(`/api/plr/${id}/fechar`);
      return response.data;
    } catch (error) {
      console.error('Erro ao fechar período:', error);
      throw error;
    }
  }

  /**
   * Processa os resultados de uma regra de PLR
   */
  async processarResultados(id: string): Promise<RegraPLR> {
    try {
      const response = await api.put(`/api/plr/${id}/processar`);
      return response.data;
    } catch (error) {
      console.error('Erro ao processar resultados:', error);
      throw error;
    }
  }

  /**
   * Obtém resultados de PLR para o colaborador logado
   */
  async obterResultadosColaborador(): Promise<ResultadoPLR[]> {
    try {
      const response = await api.get('/api/plr/resultados/colaborador');
      return response.data;
    } catch (error) {
      console.error('Erro ao obter resultados do colaborador:', error);
      throw error;
    }
  }

  /**
   * Obtém resultados de uma regra específica
   */
  async obterResultadosRegra(regraId: string): Promise<ResultadoPLR[]> {
    try {
      const response = await api.get(`/api/plr/${regraId}/resultados`);
      return response.data;
    } catch (error) {
      console.error('Erro ao obter resultados da regra:', error);
      throw error;
    }
  }

  /**
   * Gera uma regra de PLR usando IA
   */
  async gerarRegra(dados: PLRGeracaoDto): Promise<RegraPLR> {
    try {
      const response = await api.post('/api/plr/gerar', dados);
      return response.data;
    } catch (error) {
      console.error('Erro ao gerar regra de PLR:', error);
      throw error;
    }
  }
}

export default new PLRService();
